//
// era5.ts — Copernicus Climate Data Store, ERA5 reanalysis
//
// Translation of `Networking/ERA5Service.swift`. The CDS key never enters
// the browser. A script with the key writes a compact JSON grid, and both
// apps fetch it from axispace.org.
//

import { fetchJSON } from "./config";

export const ERA5_URL = "/feeds/era5/current.json";

export interface ERA5Grid {
  source: string;
  validAt: string;
  step: number;
  latMax: number;
  lonMin: number;
  nLat: number;
  nLon: number;
  tC: number[];
  u: number[];
  v: number[];
  cloud: number[];
  precipMm: number[];
}

function at(grid: ERA5Grid, values: number[], row: number, col: number): number {
  return values[row * grid.nLon + col] ?? 0;
}

export function sample(grid: ERA5Grid, values: number[], lat: number, lon: number): number {
  if (grid.nLat < 2 || grid.nLon < 2 || values.length !== grid.nLat * grid.nLon) return 0;
  let wrapped = lon;
  while (wrapped < grid.lonMin) wrapped += 360;
  while (wrapped >= grid.lonMin + 360) wrapped -= 360;
  const rowF = (grid.latMax - lat) / grid.step;
  const colF = (wrapped - grid.lonMin) / grid.step;
  const r0 = Math.min(grid.nLat - 2, Math.max(0, Math.floor(rowF)));
  const c0 = Math.floor(colF);
  const fr = Math.min(1, Math.max(0, rowF - r0));
  const fc = colF - Math.floor(colF);
  const c0n = ((c0 % grid.nLon) + grid.nLon) % grid.nLon;
  const c1 = (c0n + 1) % grid.nLon;
  const v00 = at(grid, values, r0, c0n);
  const v10 = at(grid, values, r0, c1);
  const v01 = at(grid, values, r0 + 1, c0n);
  const v11 = at(grid, values, r0 + 1, c1);
  return (1 - fr) * ((1 - fc) * v00 + fc * v10) + fr * ((1 - fc) * v01 + fc * v11);
}

export function windAt(grid: ERA5Grid, lat: number, lon: number): { u: number; v: number } {
  return { u: sample(grid, grid.u, lat, lon), v: sample(grid, grid.v, lat, lon) };
}

export async function fetchERA5(): Promise<ERA5Grid | null> {
  const grid = await fetchJSON<ERA5Grid>(ERA5_URL, 20000);
  if (!grid?.tC?.length || !grid.nLat || !grid.nLon) return null;
  if (!grid.cloud) grid.cloud = [];
  if (!grid.precipMm) grid.precipMm = [];
  return grid;
}

export type Era5Field = "temp" | "cloud" | "precip";

function tempColor(c: number): [number, number, number, number] {
  const t = Math.min(45, Math.max(-40, c));
  let r: number, g: number, b: number;
  if (t < 0) {
    const u = (t + 40) / 40;
    r = 30 + 150 * u;
    g = 70 + 140 * u;
    b = 180 + 50 * u;
  } else if (t < 20) {
    const u = t / 20;
    r = 180 + 75 * u;
    g = 210 - 20 * u;
    b = 230 - 150 * u;
  } else {
    const u = Math.min(1, (t - 20) / 20);
    r = 255 - 20 * u;
    g = 190 - 150 * u;
    b = 80 - 50 * u;
  }
  return [r, g, b, 140];
}

/** Paint a Gleason disc once. Never from the animation loop. */
export function raster(
  grid: ERA5Grid,
  field: Era5Field,
  unproject: (
    x: number, y: number, cx: number, cy: number, r: number,
  ) => { lat: number; lon: number } | null,
  size = 384,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  const img = ctx.createImageData(size, size);
  const data = img.data;
  const cx = size / 2;
  const cy = size / 2;
  const mapRadius = size / 2;
  const cloudVals = grid.cloud.map(Number);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const geo = unproject(x, y, cx, cy, mapRadius);
      if (!geo) continue;
      if (field === "temp") {
        const [r, g, b, a] = tempColor(sample(grid, grid.tC, geo.lat, geo.lon));
        data[i] = r; data[i + 1] = g; data[i + 2] = b; data[i + 3] = a;
      } else if (field === "cloud") {
        const c = sample(grid, cloudVals, geo.lat, geo.lon) / 100;
        data[i] = 230; data[i + 1] = 240; data[i + 2] = 255;
        data[i + 3] = Math.min(180, Math.max(0, c * 180));
      } else {
        const p = sample(grid, grid.precipMm, geo.lat, geo.lon);
        if (p < 0.15) continue;
        data[i] = 15; data[i + 1] = 184; data[i + 2] = 160;
        data[i + 3] = Math.min(200, 40 + p * 40);
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}
